'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react'

import { ViewEventEventTypeEnum } from '@b-stock/buyer-personalization-api-client'
import {
  useAccessToken,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import type { ListingView } from '@b-stock/search-api-client'

import { FeatureFlags } from '@/helpers/featureFlags'
import getBuyerViewHistoryClient from '@/queries/getBuyerViewHistoryClient'
import getListingViewsClient from '@/queries/getListingViewsClient'

const FLUSH_INTERVAL_MS = 2 * 60 * 1000
const SESSION_KEY = 'listing_view_queue'

type QueuedListingView = {
  listingId: string
  timestamp: string
}

const readQueue = (): QueuedListingView[] => {
  const raw = sessionStorage.getItem(SESSION_KEY)
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as QueuedListingView[]) : []
  } catch {
    return []
  }
}

const aggregateViewCounts = (queue: QueuedListingView[]): ListingView[] => {
  const counts = new Map<string, number>()
  for (const { listingId } of queue) {
    counts.set(listingId, (counts.get(listingId) ?? 0) + 1)
  }
  return Array.from(counts, ([listingId, viewCount]) => ({
    listingId,
    viewCount,
  }))
}

type ListingViewTrackingContextValue = {
  trackListingView: (listingId: string) => void
}

const ListingViewTrackingContext =
  createContext<ListingViewTrackingContextValue>({
    trackListingView: () => {},
  })

export function useListingViewTracking() {
  return useContext(ListingViewTrackingContext)
}

export function ListingViewTrackingProvider({
  children,
}: {
  children: ReactNode
}) {
  const accessToken = useAccessToken()
  const accountId = useDecodedAccessToken()?.accountId
  const isEnabled = useFlag(FeatureFlags.ENABLE_LISTING_VIEW_TRACKING)

  const stateRef = useRef({ accessToken, accountId, isEnabled })
  stateRef.current = { accessToken, accountId, isEnabled } // oxlint-disable-line react/refs

  const flush = useCallback(() => {
    const { accessToken, accountId, isEnabled } = stateRef.current
    if (!isEnabled || !accountId) return

    const queue = readQueue()
    sessionStorage.removeItem(SESSION_KEY)
    if (queue.length === 0) return

    const axiosParams = buildAxiosRequestConfig({ accessToken })

    getListingViewsClient()
      .recordListingViewsBatch(
        { listingViewsBatchRequest: { views: aggregateViewCounts(queue) } },
        axiosParams
      )
      .catch(() => {
        // fire-and-forget: recording views is non-critical
      })

    getBuyerViewHistoryClient()
      .postViewEvents(
        {
          accountId,
          viewEvent: queue.map(({ listingId, timestamp }) => ({
            listingId,
            eventType: ViewEventEventTypeEnum.View,
            timestamp,
          })),
        },
        axiosParams
      )
      .catch(() => {
        // fire-and-forget: recording views is non-critical
      })
  }, [])

  useEffect(() => {
    const interval = setInterval(flush, FLUSH_INTERVAL_MS)
    window.addEventListener('pagehide', flush)
    return () => {
      flush()
      clearInterval(interval)
      window.removeEventListener('pagehide', flush)
    }
  }, [flush])

  const trackListingView = useCallback((listingId: string) => {
    if (!stateRef.current.isEnabled || !stateRef.current.accountId) return
    try {
      const queue = readQueue()
      queue.push({ listingId, timestamp: new Date().toISOString() })
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(queue))
    } catch {
      // sessionStorage unavailable or quota exceeded -- skip silently
    }
  }, [])

  return (
    <ListingViewTrackingContext.Provider value={{ trackListingView }}>
      {children}
    </ListingViewTrackingContext.Provider>
  )
}
